import { adTechLog } from '../../general/log';
import { updateAdSlotEventClassName } from './ad-slot-class-names';
import { getAdSlots, updateAdSlotValue } from "../../general/ad-slot-storage";

/**
 * This event is fired when an ad response has been received for a particular slot.
 *
 * See: https://developers.google.com/publisher-tag/reference#googletag.events.slotresponsereceived
 *
 * @param {String} adSlotID The element ID.
 */
const slotResponseReceived = ( adSlotID ) => {

	window.googletag.pubads().addEventListener( 'slotResponseReceived', ( e ) => {

		// Trigger custom event based of the main event.
		if ( e.slot.getSlotElementId() === adSlotID ) {

			const element = document.querySelector( `#${ adSlotID }` );

			if ( null !== element ) {
				adTechLog( {
					type: 'event',
					message: `Event: "slotResponseReceived", triggered for ${ adSlotID }`,
					details: {
						adEvent: e,
						adSlotID,
					},
				} );

				const event = new CustomEvent( 'slotResponseReceived', {
					detail: {
						message: 'Ad Slot has loaded.',
						adEvent: e,
						adSlotID,
					},
				} );
				element.dispatchEvent( event );
			}
		}
	} );
};

/**
 * This event is fired when the creative code is injected into a slot. This event will occur
 * before the creative's resources are fetched, so the creative may not be visible yet. If you
 * need to know when all creative resources for a slot have finished loading, consider the
 * SlotOnloadEvent instead.
 *
 * See: https://developers.google.com/publisher-tag/reference#googletag.events.slotrenderendedevent
 *
 * @param {String} adSlotID The element ID.
 */
const slotRenderEnded = ( adSlotID ) => {

	window.googletag.pubads().addEventListener( 'slotRenderEnded', ( e ) => {

		// Trigger custom event based of the main event.
		if ( e.slot.getSlotElementId() === adSlotID ) {

			const element = document.querySelector( `#${ adSlotID }` );

			if ( null !== element ) {
				adTechLog( {
					type: 'event',
					message: `Event: "slotRenderEnded", triggered for ${ adSlotID }`,
					details: {
						adEvent: e,
						adSlotID,
					},
				} );

				const event = new CustomEvent( 'slotRenderEnded', {
					detail: {
						message: 'Ad Slot has rendered.',
						adEvent: e,
						adSlotID,
					},
				} );
				element.dispatchEvent( event );
			}
		}
	} );
};

/**
 * This event is fired when the creative's iframe fires its load event. When rendering rich
 * media ads in sync rendering mode, no iframe is used so no SlotOnloadEvent will be fired.
 *
 * See: https://developers.google.com/publisher-tag/reference#googletag.events.slotonloadevent
 *
 * @param {String} adSlotID The element ID.
 */
const slotOnload = ( adSlotID ) => {

	window.googletag.pubads().addEventListener( 'slotOnload', ( e ) => {

		// Trigger custom event based of the main event.
		if ( e.slot.getSlotElementId() === adSlotID ) {
			const element = document.querySelector( `#${ adSlotID }` );

			if ( null !== element ) {
				adTechLog( {
					type: 'event',
					message: `Event: "slotOnload", triggered for ${ adSlotID }`,
					details: {
						adEvent: e,
						adSlotID,
					},
				} );

				const event = new CustomEvent( 'slotOnload', {
					detail: {
						message: 'Ad Slot has loaded.',
						adEvent: e,
						adSlotID,
					},
				} );
				element.dispatchEvent( event );
			}
		}
	} );
};

/**
 * This event is fired when an impression becomes viewable, according to the Active View criteria.
 *
 * See Active View criteria: https://support.google.com/admanager/answer/4524488
 * See: https://developers.google.com/publisher-tag/reference#googletag.events.impressionviewableevent
 *
 * @param {String} adSlotID The element ID.
 */
const impressionViewable = ( adSlotID ) => {
	window.googletag.pubads().addEventListener( 'impressionViewable', ( e ) => {

		// Trigger custom event based of the main event.
		if ( e.slot.getSlotElementId() === adSlotID ) {
			const element = document.querySelector( `#${ adSlotID }` );

			if ( null !== element ) {
				adTechLog( {
					type: 'event',
					message: `Event: "impressionViewable", triggered for ${ adSlotID }`,
					details: {
						adEvent: e,
						adSlotID,
					},
				} );

				const event = new CustomEvent( 'impressionViewable', {
					detail: {
						message: 'Ad Slot is viewable.',
						adEvent: e,
						adSlotID,
					},
				} );
				element.dispatchEvent( event );
			}
		}
	} );
};

const adEvents = ( adSlotID ) => {
	const element = document.querySelector( `#${ adSlotID }` );

	if ( null !== element ) {
		// Update the slot.
		updateAdSlotValue( 'googletag', adSlotID, 'events', true );

		// Add the actual events listening for ad changes, to the ad slot element.
		slotResponseReceived( adSlotID )
		slotRenderEnded( adSlotID );
		impressionViewable( adSlotID );
		slotOnload( adSlotID );

		// The event listeners for specific plugin changes.
		element.addEventListener( 'slotResponseReceived', ( e ) => {
			const { detail } = e;

			// Bail early and log the details if the param is missing.
			if ( ! detail ) {
				adTechLog( {
					type: 'error',
					message: `Event: "slotResponseReceived", detail not found in e.`,
					details: {
						e,
					},
				} );
				return;
			}

			const { adSlotID } = detail;

			// Bail early and log the details if the param is missing.
			if ( ! adSlotID ) {
				adTechLog( {
					type: 'error',
					message: `Event: "slotResponseReceived", adSlotID not found in detail.`,
					details: {
						e,
					},
				} );
				return;
			}

			updateAdSlotEventClassName( adSlotID, 'received' );
			updateAdSlotValue( 'googletag', adSlotID, 'status_step_3_received', true );
		} );

		element.addEventListener( 'slotRenderEnded', ( e ) => {
			const { detail } = e;

			// Bail early and log the details if the param is missing.
			if ( ! detail ) {
				adTechLog( {
					type: 'error',
					message: `Event: "slotRenderEnded", detail not found in e.`,
					details: {
						e,
					},
				} );
				return;
			}

			const { adSlotID, adEvent } = detail;

			// Bail early and log the details if the param is missing.
			if ( ! adSlotID ) {
				adTechLog( {
					type: 'error',
					message: `Event: "slotRenderEnded", adSlotID OR adEvent not found in detail.`,
					details: {
						e,
					},
				} );
				return;
			}

			if ( adEvent.isEmpty ) {
				updateAdSlotEventClassName( adSlotID, 'rendered-empty' );
			} else {
				updateAdSlotEventClassName( adSlotID, 'rendered' );
			}

			updateAdSlotValue( 'googletag', adSlotID, 'isEmpty', adEvent.isEmpty );
			updateAdSlotValue( 'googletag', adSlotID, 'status_step_4_rendered', true );
		} );

		element.addEventListener( 'slotOnload', ( e ) => {
			const { detail } = e;

			// Bail early and log the details if the param is missing.
			if ( ! detail ) {
				adTechLog( {
					type: 'error',
					message: `Event: "slotOnload", detail not found in e.`,
					details: {
						e,
					},
				} );
				return;
			}

			const { adSlotID } = detail;

			// Bail early and log the details if the param is missing.
			if ( ! adSlotID ) {
				adTechLog( {
					type: 'error',
					message: `Event: "slotOnload", adSlotID not found in detail.`,
					details: {
						e,
					},
				} );
				return;
			}

			updateAdSlotEventClassName( e.detail.adSlotID, 'loaded' );
			updateAdSlotValue( 'googletag', e.detail.adSlotID, 'status_step_5_loaded', true );
		} );

		element.addEventListener( 'impressionViewable', ( e ) => {

			const { detail } = e;

			// Bail early and log the details if the param is missing.
			if ( ! detail ) {
				adTechLog( {
					type: 'error',
					message: `Event: "impressionViewable", detail not found in e.`,
					details: {
						e,
					},
				} );
				return;
			}

			const { adSlotID } = detail;

			// Bail early and log the details if the param is missing.
			if ( ! adSlotID ) {
				adTechLog( {
					type: 'error',
					message: `Event: "impressionViewable", adSlotID not found in detail.`,
					details: {
						e,
					},
				} );
				return;
			}

			updateAdSlotEventClassName( adSlotID, 'viewable' );
			updateAdSlotValue( 'googletag', adSlotID, 'status_step_6_viewable', true );
		} );

		element.addEventListener( 'slotDestroyed', ( e ) => {

			const { detail } = e;

			// Bail early and log the details if the param is missing.
			if ( ! detail ) {
				adTechLog( {
					type: 'error',
					message: `Event: "slotDestroyed", adEvent OR detail not found in e.`,
					details: {
						e,
					},
				} );
				return;
			}

			const { adSlotID } = detail;

			// Bail early and log the details if the param is missing.
			if ( ! adSlotID ) {
				adTechLog( {
					type: 'error',
					message: `Event: "slotDestroyed", adSlotID not found in detail.`,
					details: {
						e,
					},
				} );
				return;
			}

			updateAdSlotEventClassName( adSlotID, 'destroyed' );
			updateAdSlotValue( 'googletag', adSlotID, 'destroyed', true );
		} );
	}
}

export const adSlotEvents = ( adSlotID = null ) => {

	// If no slot ID was passed, ad the events to all those slots that have not had the events added yet.
	if ( null === adSlotID ) {

		const slots = getAdSlots( 'googletag', 'events', false );

		for ( const slot of slots ) {
			adEvents( slot.id );
		}
	} else {
		adEvents( adSlotID );
	}
}